'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Plus, BookOpen, Heart, Headphones } from 'lucide-react';
import type { Story } from '@/contexts/stories/types';
import { StoryTableView } from './StoryTableView';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ListFilters, useSearchFilter, LanguageFilter, LibraryPagination } from '@/shared/components/list';
import { listAudioStories, audioStoryKeys } from '@/contexts/audio-stories';
import { getIllustrations, illustrationKeys } from '@/contexts/illustrations';
import { ListenQueuePanel, type Narration } from './ListenQueuePanel';

interface Child {
  id: string;
  name: string;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface StoryListProps {
  initialStories: Story[];
  childrenList: Child[];
  locale: string;
  pagination?: PaginationInfo;
}

export function StoryList({ initialStories, childrenList, locale, pagination }: StoryListProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const t = useTranslations('stories');
  const tListen = useTranslations('stories.listen');
  const tRoot = useTranslations();
  const [stories, setStories] = useState<Story[]>(initialStories);
  const [isLoading, setIsLoading] = useState(false);
  const { searchValue, debouncedValue: searchQuery, setSearchValue } = useSearchFilter();
  const [languageFilter, setLanguageFilter] = useState<string>('');

  /**
   * "Only the ones I can listen to" — a view, not a query.
   *
   * It filters what the page already has rather than asking the server,
   * because what makes a story listenable is an assembled narration, which
   * lives in another table the list reads anyway.
   */
  const [onlyWithAudio, setOnlyWithAudio] = useState(false);

  // Favorites filter — persisted in URL as ?filter=favorites
  const [onlyFavorites, setOnlyFavorites] = useState<boolean>(
    () => searchParams.get('filter') === 'favorites'
  );

  // Reset loading state when new data arrives
  useEffect(() => {
    setStories(initialStories);
    setIsLoading(false);
  }, [initialStories]);

  // Derivative counts: audio stories & books per story
  const familyId = initialStories[0]?.familyId;

  const { data: audioData } = useQuery({
    queryKey: audioStoryKeys.list(familyId ?? ''),
    queryFn: () => listAudioStories(),
    enabled: !!familyId,
    staleTime: 30_000,
  });

  const { data: booksData } = useQuery({
    queryKey: illustrationKeys.list(familyId ?? ''),
    queryFn: () => getIllustrations(),
    enabled: !!familyId,
    staleTime: 30_000,
  });

  const audioCountByStory = useMemo(() => {
    if (!audioData?.success || !audioData.data?.audioStories) return {};
    return audioData.data.audioStories.reduce<Record<string, number>>((acc, a) => {
      acc[a.storyId] = (acc[a.storyId] ?? 0) + 1;
      return acc;
    }, {});
  }, [audioData]);

  const firstAudioUrlByStory = useMemo(() => {
    if (!audioData?.success || !audioData.data?.audioStories) return {};
    const map: Record<string, string> = {};
    for (const a of audioData.data.audioStories) {
      if (a.assembledAudioUrl && !map[a.storyId]) {
        map[a.storyId] = a.assembledAudioUrl;
      }
    }
    return map;
  }, [audioData]);

  /**
   * Every finished narration in the library, as something playable.
   *
   * One line per narration, not per story: a story read by two voices is two
   * things to listen to, and picking for the parent would be picking wrong.
   */
  const narrations = useMemo<Narration[]>(() => {
    if (!audioData?.success || !audioData.data?.audioStories) return [];
    const titleById = new Map(stories.map((st) => [st.id, st.title] as const));
    return audioData.data.audioStories
      .filter((a) => !!a.assembledAudioUrl && titleById.has(a.storyId))
      .map((a) => ({
        id: a.id,
        storyId: a.storyId,
        audioUrl: a.assembledAudioUrl!,
        title: titleById.get(a.storyId) ?? a.name ?? '',
        subtitle: a.name ?? a.defaultVoiceName ?? undefined,
        seconds: a.audioBlocks.reduce((sum, b) => sum + (b.duration ?? 0), 0),
      }));
  }, [audioData, stories]);

  const storiesWithAudio = useMemo(
    () => new Set(narrations.map((n) => n.storyId)),
    [narrations],
  );

  const bookCountByStory = useMemo(() => {
    if (!booksData) return {};
    return booksData.reduce<Record<string, number>>((acc, b) => {
      acc[b.storyId] = (acc[b.storyId] ?? 0) + 1;
      return acc;
    }, {});
  }, [booksData]);

  // Use server-provided pagination or defaults
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || 20;
  const totalItems = pagination?.total || stories.length;
  const totalPages = pagination?.totalPages || Math.ceil(totalItems / pageSize);

  // Stories are already sorted and paginated by server
  // We keep local sorting only for optimistic UI updates after deletion
  const displayStories = useMemo(() => {
    let result = [...stories].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((story) => story.title.toLowerCase().includes(query));
    }
    if (languageFilter) {
      result = result.filter((story) => story.language === languageFilter);
    }
    if (onlyWithAudio) {
      result = result.filter((story) => storiesWithAudio.has(story.id));
    }
    return result;
  }, [stories, searchQuery, languageFilter, onlyWithAudio, storiesWithAudio]);

  // Distinct languages present, for the shared language filter.
  const languageFilterOptions = useMemo(
    () => Array.from(new Set(stories.map((s) => s.language).filter(Boolean) as string[])).sort(),
    [stories],
  );

  // Handle story deletion - optimistic UI update
  const handleDeleteStory = (storyId: string) => {
    setStories((prevStories) => {
      const newStories = prevStories.filter((story) => story.id !== storyId);

      // If we deleted the last story on this page, reload or go to previous page
      if (newStories.length === 0 && currentPage > 1) {
        // Go to previous page
        handlePageChange(currentPage - 1);
      } else if (newStories.length === 0 && totalPages > 1) {
        // We're on page 1 but there are other pages - reload to get fresh data
        router.refresh();
      }

      return newStories;
    });
  };

  // Toggle favorites filter — updates URL param and resets to page 1
  const handleFavoritesFilter = () => {
    const next = !onlyFavorites;
    setOnlyFavorites(next);
    setIsLoading(true);
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', '1');
    params.set('pageSize', pageSize.toString());
    if (next) {
      params.set('filter', 'favorites');
    } else {
      params.delete('filter');
    }
    router.replace(`/${locale}/stories?${params.toString()}`);
  };

  // Navigate to new page (server-side pagination)
  const handlePageChange = (newPage: number) => {
    setIsLoading(true);
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', newPage.toString());
    params.set('pageSize', pageSize.toString());
    router.push(`/${locale}/stories?${params.toString()}`);
  };

  const handlePageSizeChange = (newSize: number) => {
    setIsLoading(true);

    // Save to localStorage for preference persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('stories-page-size', newSize.toString());
    }

    // Navigate to first page with new size
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', '1');
    params.set('pageSize', newSize.toString());
    router.push(`/${locale}/stories?${params.toString()}`);
  };

  // Validate page number - redirect to last valid page if needed
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      handlePageChange(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div data-testid="story-list" className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground">{t('title')}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {totalItems === 0
              ? t('noStories')
              : searchQuery.trim()
                ? t('searchResults', { count: displayStories.length })
                : t('found', { count: totalItems })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Create button */}
          <Button
            data-testid="story-create-button"
            onClick={() => router.push(`/${locale}/create`)}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('createButton')}</span>
          </Button>
        </div>
      </div>

      {/* Search + filters. Favorites toggle lives inside ListFilters for
          consistency with books & audio-stories pages. Hidden when the library
          is empty, but kept visible while the favorites filter is active so
          the user can toggle it off even if zero favorites match. */}
      {!isLoading && (totalItems > 0 || onlyFavorites) && (
        <ListFilters
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t('search')}
          searchTestId="story-search-input"
        >
          <Button
            variant={onlyFavorites ? 'default' : 'outline'}
            size="sm"
            className="h-8 sm:h-9 gap-1.5 text-xs sm:text-sm"
            onClick={handleFavoritesFilter}
            data-testid="story-favorites-filter-toggle"
            aria-pressed={onlyFavorites}
          >
            <Heart className={`h-3.5 w-3.5 ${onlyFavorites ? 'fill-current' : ''}`} />
            <span className="hidden sm:inline">{t('favorites.onlyFavorites')}</span>
          </Button>
          {/* Only offered when there is something to hear. */}
          {narrations.length > 0 && (
            <Button
              variant={onlyWithAudio ? 'default' : 'outline'}
              size="sm"
              className="h-8 gap-1.5 text-xs sm:h-9 sm:text-sm"
              onClick={() => setOnlyWithAudio((v) => !v)}
              data-testid="story-audio-filter-toggle"
              aria-pressed={onlyWithAudio}
            >
              <Headphones className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{tListen('filter')}</span>
            </Button>
          )}
          <LanguageFilter
            value={languageFilter}
            onChange={setLanguageFilter}
            options={languageFilterOptions}
            testId="story-language-filter"
          />
        </ListFilters>
      )}

      {/* The queue: visible while the narration filter is on, so the list above
          and the tracks below are the same set of stories. */}
      {!isLoading && onlyWithAudio && (
        <ListenQueuePanel
          items={narrations.filter((n) => displayStories.some((st) => st.id === n.storyId))}
        />
      )}

      {/* Loading state - Skeleton */}
      {isLoading && (
        <div className="space-y-3">
          {/* Mobile skeleton */}
          <div className="md:hidden space-y-3">
            {/* Selection bar skeleton */}
            <Skeleton className="h-10 w-full rounded-lg" />
            {/* Card skeletons */}
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <Skeleton className="w-5 h-5 rounded" />
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <div className="flex gap-2">
                      <Skeleton className="h-3 w-16" />
                      <Skeleton className="h-3 w-12" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-16" />
                      <div className="flex gap-1">
                        <Skeleton className="h-8 w-8 rounded" />
                        <Skeleton className="h-8 w-8 rounded" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table skeleton */}
          <div className="hidden md:block bg-card rounded-lg border border-border overflow-hidden">
            {/* Header */}
            <div className="bg-muted border-b border-border px-4 py-3 flex gap-4">
              <Skeleton className="w-4 h-4 rounded" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-20 ml-auto" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
            </div>
            {/* Rows */}
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="border-b border-border px-4 py-3 flex items-center gap-4">
                <Skeleton className="w-4 h-4 rounded" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
                <div className="flex gap-1">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stories table view */}
      {!isLoading && displayStories.length > 0 && (
        <>
          <StoryTableView
            stories={displayStories}
            locale={locale}
            onDelete={handleDeleteStory}
            audioCountByStory={audioCountByStory}
            bookCountByStory={bookCountByStory}
            firstAudioUrlByStory={firstAudioUrlByStory}
          />

          <LibraryPagination
            page={currentPage}
            pageSize={pageSize}
            total={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizes={[10, 20, 50, 100]}
            testIdPrefix="story"
          />
        </>
      )}

      {/* Empty state — favorites filter active, no favorites yet */}
      {!isLoading && displayStories.length === 0 && onlyFavorites && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <Heart className="w-20 h-20 mb-4 text-muted-foreground/30 stroke-[1.5]" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('favorites.noFavoritesYet')}
          </h3>
          <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
            {t('favorites.noFavoritesHint')}
          </p>
          <Button
            variant="outline"
            onClick={handleFavoritesFilter}
            data-testid="story-favorites-filter-clear"
          >
            {t('favorites.clearFavoritesFilter')}
          </Button>
        </div>
      )}

      {/* Empty state — no stories at all */}
      {!isLoading && displayStories.length === 0 && !onlyFavorites && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <BookOpen className="w-20 h-20 mb-4 text-muted-foreground/30 stroke-[1.5]" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('emptyTitle')}
          </h3>
          <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
            {t('emptyMessage')}
          </p>
          <Button
            onClick={() => router.push(`/${locale}/create`)}
            size="lg"
          >
            {t('createFirst')}
          </Button>
        </div>
      )}

    </div>
  );
}
